// src/components/settings/PetSizesTab.tsx

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography, Alert, Table, TableRow } from '@mui/material';
import { referenceDataAPI, PetType, PetSize } from '../../services/api';
import { useErrorHandler } from '../../errors';
import { useGlobalToast } from '../../App';
import {
  ConfirmDeleteDialog,
  RefCountChip,
  SystemChip,
} from './shared/SettingsElements';
import SettingsHeaderRow from './shared/SettingsHeaderRow';
import LoadingSpinner from '../common/LoadingSpinner';
import SettingsTableActions from './shared/SettingsTableActions';
import SimilarityDialog from '../forms/shared/SimilarityDialog';
import { checkReferenceDataSimilarity } from '../../utils/referenceDataSimilarityHelper';
import {
  StyledTableContainer,
  StyledTableHead,
  StyledTableHeaderCell,
  StyledTableBody,
  StyledTableRow,
  StyledTableCell,
  StyledTableChipCell,
  StyledTableChipCellContent,
  StyledTableActionsCell,
} from './shared/SettingsTableElements';
import PetSizeDialog from './shared/dialogs/PetSizeDialog';

interface PetSizesTabProps {
  usage: Record<number, number>;
  onDataChanged: () => void;
}

const PetSizesTab: React.FC<PetSizesTabProps> = ({ usage, onDataChanged }) => {
  const [items, setItems] = useState<PetSize[]>([]);
  const [petTypes, setPetTypes] = useState<PetType[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<PetSize | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [searchText, setSearchText] = useState('');
  const [showSimilarityDialog, setShowSimilarityDialog] = useState(false);
  const [similarItem, setSimilarItem] = useState<PetSize | null>(null);
  const [pendingSave, setPendingSave] = useState<{
    size: string;
    petTypeFk: number;
    active?: boolean;
  } | null>(null);
  const { handleError } = useErrorHandler();
  const { showSuccess, showError } = useGlobalToast();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [sizes, types] = await Promise.all([
        referenceDataAPI.getPetSizes(),
        referenceDataAPI.getPetTypes(),
      ]);
      setItems(sizes);
      setPetTypes(types);
    } catch (e) {
      handleError(e);
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filtered = useMemo(() => {
    if (!searchText) return items;
    const lower = searchText.toLowerCase();
    return items.filter(
      (i) =>
        i.size.toLowerCase().includes(lower) ||
        i.petType?.type?.toLowerCase().includes(lower)
    );
  }, [items, searchText]);

  const openDialog = (item?: PetSize) => {
    setEditing(item ?? null);
    setDialogOpen(true);
  };

  const proceedWithSave = async (formData: {
    size: string;
    petTypeFk: number;
    active?: boolean;
  }) => {
    try {
      if (editing) {
        await referenceDataAPI.updatePetSize(editing.id, formData);
        showSuccess();
      } else {
        await referenceDataAPI.createPetSize(formData);
        showSuccess();
      }
      await fetchData();
      onDataChanged();
      setDialogOpen(false);
    } catch (e) {
      handleError(e);
    }
  };

  const save = async (formData: {
    size: string;
    petTypeFk: number;
    active?: boolean;
  }) => {
    const scopedItems = items.filter((i) => i.petTypeFk === formData.petTypeFk);
    const { isExactMatch, match } = checkReferenceDataSimilarity(
      formData.size,
      scopedItems,
      (i) => i.size,
      (i) => i.id,
      editing?.id
    );

    if (isExactMatch) {
      showError('A size with this name already exists for this pet type');
      return;
    }

    if (match) {
      setSimilarItem(match);
      setPendingSave(formData);
      setShowSimilarityDialog(true);
      return;
    }

    await proceedWithSave(formData);
  };

  const handleUseExisting = () => {
    setShowSimilarityDialog(false);
    setSimilarItem(null);
    setPendingSave(null);
    setDialogOpen(false);
  };

  const handleCreateAnyway = async () => {
    setShowSimilarityDialog(false);
    setSimilarItem(null);
    if (pendingSave) {
      await proceedWithSave(pendingSave);
    }
    setPendingSave(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await referenceDataAPI.deletePetSize(deleteTarget.id);
      showSuccess();
      await fetchData();
      onDataChanged();
    } catch (e) {
      handleError(e);
    } finally {
      setDeleteTarget(null);
    }
  };

  if (loading)
    return <LoadingSpinner message="Loading Pet Sizes..." size={80} />;

  return (
    <Box>
      <SettingsHeaderRow
        title="Sizes"
        onAdd={() => openDialog()}
        addLabel="Add Pet Size"
        buttonMinWidth="260px"
        onSearch={setSearchText}
        onClearSearch={() => setSearchText('')}
        searchText={searchText}
        searchPlaceholder="Search sizes..."
      />
      {items.length === 0 ? (
        <Alert severity="info">No pet sizes found.</Alert>
      ) : filtered.length === 0 ? (
        <Alert severity="info">No sizes match "{searchText}".</Alert>
      ) : (
        <StyledTableContainer>
          <Table size="small">
            <StyledTableHead>
              <TableRow>
                <StyledTableHeaderCell>Type</StyledTableHeaderCell>
                <StyledTableHeaderCell>Size</StyledTableHeaderCell>
                <StyledTableHeaderCell align="center">
                  Refs
                </StyledTableHeaderCell>
                <StyledTableHeaderCell align="right">
                  Actions
                </StyledTableHeaderCell>
              </TableRow>
            </StyledTableHead>
            <StyledTableBody>
              {filtered.map((item, index) => {
                const refCount = usage[item.id] ?? 0;
                return (
                  <StyledTableRow key={item.id} isOdd={index % 2 === 0}>
                    <StyledTableCell>
                      <Typography>{item.petType?.type ?? '-'}</Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography>{item.size}</Typography>
                        {item.isSystem && <SystemChip />}
                      </Box>
                    </StyledTableCell>
                    <StyledTableChipCell>
                      <StyledTableChipCellContent>
                        <RefCountChip count={refCount} />
                      </StyledTableChipCellContent>
                    </StyledTableChipCell>
                    <StyledTableActionsCell align="right">
                      <SettingsTableActions
                        itemId={item.id}
                        itemName={item.size}
                        refCount={refCount}
                        isSystem={item.isSystem}
                        onEdit={() => openDialog(item)}
                        onDelete={() =>
                          setDeleteTarget({ id: item.id, name: item.size })
                        }
                        refLabel="booking"
                      />
                    </StyledTableActionsCell>
                  </StyledTableRow>
                );
              })}
            </StyledTableBody>
          </Table>
        </StyledTableContainer>
      )}

      <PetSizeDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={save}
        editing={editing}
        petTypes={petTypes}
      />

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        title="Delete pet size?"
        message={`Are you sure you want to delete "${deleteTarget?.name}"?`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <SimilarityDialog
        open={showSimilarityDialog}
        entityType="petSize"
        similarEntity={similarItem}
        isEditMode={!!editing}
        onUseExisting={handleUseExisting}
        onCreateNew={handleCreateAnyway}
        onClose={() => setShowSimilarityDialog(false)}
      />
    </Box>
  );
};

export default PetSizesTab;
